// eslint-disable-next-line jira/filename-case
import type { LocalViewId, ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	createStateHook,
	createSelector,
	type Action,
	type BoundActions,
	type HookFunction,
	type HookStateFunction,
} from '@atlassian/react-sweet-state';
import { findView } from './updateContainerState.tsx';
import { NavigationStore } from './useNavigationActions.tsx';
import type { NavigationView, Props, State } from './NavigationView.types.tsx';
import {
	getContainerViewsCount,
	getHasReachedLimit,
	type ContainerProps,
	type EntityLimitsHookProp,
} from './getHasReachedLimit.tsx';

const EMPTY_VIEWS: NavigationView[] = [];

export const useNavigationViews: HookFunction<
	NavigationView[],
	BoundActions<
		State,
		{
			loadViews: ({ tryPreload }?: { tryPreload?: boolean }) => Action<State, Props>;
			refreshView: (viewUUID: string) => Action<State, Props>;
			refreshViews: () => Action<State, Props>;
			createViewSet: (localId: LocalViewId, name: string) => Action<State, Props>;
			createView: (viewType: ViewKind, parentId?: LocalViewId) => Action<State, Props>;
			insertViewSet: () => Action<State, Props>;
			deleteViewSet: (localId: LocalViewId) => Action<State, Props>;
			updateViewSet: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameViewSet: (localId: LocalViewId) => Action<State, Props>;
			cancelViewSetRenaming: (localId: LocalViewId) => Action<State, Props>;
			deleteView: (localId: LocalViewId) => Action<State, Props>;
			updateView: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameView: (localId: LocalViewId) => Action<State, Props>;
			cancelViewRenaming: (localId: LocalViewId) => Action<State, Props>;
			addEmoji: (emojiId: string | undefined, localId: LocalViewId) => Action<State, Props>;
			deleteEmoji: (localId: LocalViewId) => Action<State, Props>;
			cloneView: (viewId: LocalViewId, viewType: ViewKind) => Action<State, Props>;
			toggleView: (localId: LocalViewId) => Action<State, Props>;
			rankView: (
				rankPayload: {
					id: string;
					parentId?: string;
					srcParentId?: string;
					index: number;
					isExpandedBeforeDrag: boolean;
				},
				onActionSuccess?: () => void,
				onActionError?: (err?: Error) => void,
			) => Action<State, Props>;
			syncViewName: (viewId: string, type: ViewKind, name: string) => Action<State, Props>;
			syncViewEmoji: (
				viewId: string,
				type: ViewKind,
				emoji: string | undefined,
			) => Action<State, Props>;
			syncViewDeletion: (viewId: string, type: ViewKind) => Action<State, Props>;
			expandSectionForSelectedView: () => Action<State, Props>;
			deleteViewFromState: (viewUUID: string) => Action<State, Props>;
			updateEntityLimits: () => Action<State, Props>;
		}
	>,
	{
		containerId: string;
	}
> = createHook(NavigationStore, {
	selector: createSelector(
		(state, { containerId }: { containerId: string }) => state.containers[containerId],
		(containerState) => {
			if (containerState) {
				return containerState.views;
			}

			return EMPTY_VIEWS;
		},
	),
});

export const useIsNavigationLoading: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			loadViews: ({ tryPreload }?: { tryPreload?: boolean }) => Action<State, Props>;
			refreshView: (viewUUID: string) => Action<State, Props>;
			refreshViews: () => Action<State, Props>;
			createViewSet: (localId: LocalViewId, name: string) => Action<State, Props>;
			createView: (viewType: ViewKind, parentId?: LocalViewId) => Action<State, Props>;
			insertViewSet: () => Action<State, Props>;
			deleteViewSet: (localId: LocalViewId) => Action<State, Props>;
			updateViewSet: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameViewSet: (localId: LocalViewId) => Action<State, Props>;
			cancelViewSetRenaming: (localId: LocalViewId) => Action<State, Props>;
			deleteView: (localId: LocalViewId) => Action<State, Props>;
			updateView: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameView: (localId: LocalViewId) => Action<State, Props>;
			cancelViewRenaming: (localId: LocalViewId) => Action<State, Props>;
			addEmoji: (emojiId: string | undefined, localId: LocalViewId) => Action<State, Props>;
			deleteEmoji: (localId: LocalViewId) => Action<State, Props>;
			cloneView: (viewId: LocalViewId, viewType: ViewKind) => Action<State, Props>;
			toggleView: (localId: LocalViewId) => Action<State, Props>;
			rankView: (
				rankPayload: {
					id: string;
					parentId?: string;
					srcParentId?: string;
					index: number;
					isExpandedBeforeDrag: boolean;
				},
				onActionSuccess?: () => void,
				onActionError?: (err?: Error) => void,
			) => Action<State, Props>;
			syncViewName: (viewId: string, type: ViewKind, name: string) => Action<State, Props>;
			syncViewEmoji: (
				viewId: string,
				type: ViewKind,
				emoji: string | undefined,
			) => Action<State, Props>;
			syncViewDeletion: (viewId: string, type: ViewKind) => Action<State, Props>;
			expandSectionForSelectedView: () => Action<State, Props>;
			deleteViewFromState: (viewUUID: string) => Action<State, Props>;
			updateEntityLimits: () => Action<State, Props>;
		}
	>,
	{
		containerId: string;
	}
> = createHook(NavigationStore, {
	selector: createSelector(
		(state, { containerId }: { containerId: string }) => state.containers[containerId],
		(containerState) => containerState?.isLoading || false,
	),
});

export const useIsNavigationInitialized: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			loadViews: ({ tryPreload }?: { tryPreload?: boolean }) => Action<State, Props>;
			refreshView: (viewUUID: string) => Action<State, Props>;
			refreshViews: () => Action<State, Props>;
			createViewSet: (localId: LocalViewId, name: string) => Action<State, Props>;
			createView: (viewType: ViewKind, parentId?: LocalViewId) => Action<State, Props>;
			insertViewSet: () => Action<State, Props>;
			deleteViewSet: (localId: LocalViewId) => Action<State, Props>;
			updateViewSet: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameViewSet: (localId: LocalViewId) => Action<State, Props>;
			cancelViewSetRenaming: (localId: LocalViewId) => Action<State, Props>;
			deleteView: (localId: LocalViewId) => Action<State, Props>;
			updateView: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameView: (localId: LocalViewId) => Action<State, Props>;
			cancelViewRenaming: (localId: LocalViewId) => Action<State, Props>;
			addEmoji: (emojiId: string | undefined, localId: LocalViewId) => Action<State, Props>;
			deleteEmoji: (localId: LocalViewId) => Action<State, Props>;
			cloneView: (viewId: LocalViewId, viewType: ViewKind) => Action<State, Props>;
			toggleView: (localId: LocalViewId) => Action<State, Props>;
			rankView: (
				rankPayload: {
					id: string;
					parentId?: string;
					srcParentId?: string;
					index: number;
					isExpandedBeforeDrag: boolean;
				},
				onActionSuccess?: () => void,
				onActionError?: (err?: Error) => void,
			) => Action<State, Props>;
			syncViewName: (viewId: string, type: ViewKind, name: string) => Action<State, Props>;
			syncViewEmoji: (
				viewId: string,
				type: ViewKind,
				emoji: string | undefined,
			) => Action<State, Props>;
			syncViewDeletion: (viewId: string, type: ViewKind) => Action<State, Props>;
			expandSectionForSelectedView: () => Action<State, Props>;
			deleteViewFromState: (viewUUID: string) => Action<State, Props>;
			updateEntityLimits: () => Action<State, Props>;
		}
	>,
	{
		containerId: string;
	}
> = createHook(NavigationStore, {
	selector: createSelector(
		(state, { containerId }: { containerId: string }) => state.containers[containerId],
		(containerState) => containerState?.initialized || false,
	),
});

export const useIsNavigationLocked: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			loadViews: ({ tryPreload }?: { tryPreload?: boolean }) => Action<State, Props>;
			refreshView: (viewUUID: string) => Action<State, Props>;
			refreshViews: () => Action<State, Props>;
			createViewSet: (localId: LocalViewId, name: string) => Action<State, Props>;
			createView: (viewType: ViewKind, parentId?: LocalViewId) => Action<State, Props>;
			insertViewSet: () => Action<State, Props>;
			deleteViewSet: (localId: LocalViewId) => Action<State, Props>;
			updateViewSet: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameViewSet: (localId: LocalViewId) => Action<State, Props>;
			cancelViewSetRenaming: (localId: LocalViewId) => Action<State, Props>;
			deleteView: (localId: LocalViewId) => Action<State, Props>;
			updateView: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameView: (localId: LocalViewId) => Action<State, Props>;
			cancelViewRenaming: (localId: LocalViewId) => Action<State, Props>;
			addEmoji: (emojiId: string | undefined, localId: LocalViewId) => Action<State, Props>;
			deleteEmoji: (localId: LocalViewId) => Action<State, Props>;
			cloneView: (viewId: LocalViewId, viewType: ViewKind) => Action<State, Props>;
			toggleView: (localId: LocalViewId) => Action<State, Props>;
			rankView: (
				rankPayload: {
					id: string;
					parentId?: string;
					srcParentId?: string;
					index: number;
					isExpandedBeforeDrag: boolean;
				},
				onActionSuccess?: () => void,
				onActionError?: (err?: Error) => void,
			) => Action<State, Props>;
			syncViewName: (viewId: string, type: ViewKind, name: string) => Action<State, Props>;
			syncViewEmoji: (
				viewId: string,
				type: ViewKind,
				emoji: string | undefined,
			) => Action<State, Props>;
			syncViewDeletion: (viewId: string, type: ViewKind) => Action<State, Props>;
			expandSectionForSelectedView: () => Action<State, Props>;
			deleteViewFromState: (viewUUID: string) => Action<State, Props>;
			updateEntityLimits: () => Action<State, Props>;
		}
	>,
	{
		containerId: string;
	}
> = createHook(NavigationStore, {
	selector: createSelector(
		(state, { containerId }: { containerId: string }) => state.containers[containerId],
		(containerState) => containerState?.isLocked || false,
	),
});

export const useNavigationView: HookFunction<
	NavigationView | null,
	BoundActions<
		State,
		{
			loadViews: ({ tryPreload }?: { tryPreload?: boolean }) => Action<State, Props>;
			refreshView: (viewUUID: string) => Action<State, Props>;
			refreshViews: () => Action<State, Props>;
			createViewSet: (localId: LocalViewId, name: string) => Action<State, Props>;
			createView: (viewType: ViewKind, parentId?: LocalViewId) => Action<State, Props>;
			insertViewSet: () => Action<State, Props>;
			deleteViewSet: (localId: LocalViewId) => Action<State, Props>;
			updateViewSet: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameViewSet: (localId: LocalViewId) => Action<State, Props>;
			cancelViewSetRenaming: (localId: LocalViewId) => Action<State, Props>;
			deleteView: (localId: LocalViewId) => Action<State, Props>;
			updateView: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameView: (localId: LocalViewId) => Action<State, Props>;
			cancelViewRenaming: (localId: LocalViewId) => Action<State, Props>;
			addEmoji: (emojiId: string | undefined, localId: LocalViewId) => Action<State, Props>;
			deleteEmoji: (localId: LocalViewId) => Action<State, Props>;
			cloneView: (viewId: LocalViewId, viewType: ViewKind) => Action<State, Props>;
			toggleView: (localId: LocalViewId) => Action<State, Props>;
			rankView: (
				rankPayload: {
					id: string;
					parentId?: string;
					srcParentId?: string;
					index: number;
					isExpandedBeforeDrag: boolean;
				},
				onActionSuccess?: () => void,
				onActionError?: (err?: Error) => void,
			) => Action<State, Props>;
			syncViewName: (viewId: string, type: ViewKind, name: string) => Action<State, Props>;
			syncViewEmoji: (
				viewId: string,
				type: ViewKind,
				emoji: string | undefined,
			) => Action<State, Props>;
			syncViewDeletion: (viewId: string, type: ViewKind) => Action<State, Props>;
			expandSectionForSelectedView: () => Action<State, Props>;
			deleteViewFromState: (viewUUID: string) => Action<State, Props>;
			updateEntityLimits: () => Action<State, Props>;
		}
	>,
	{
		containerId: string;
		localId: LocalViewId;
	}
> = createHook(NavigationStore, {
	selector: createSelector(
		(state, { containerId }: { containerId: string }) => state.containers[containerId],
		(_, { localId }: { containerId: string; localId: LocalViewId }) => localId,
		(containerState, localId) => findView(containerState, localId)?.view || null,
	),
});

export const useContainerViewsCount: HookStateFunction<number, ContainerProps> = createStateHook(
	NavigationStore,
	{
		selector: getContainerViewsCount,
	},
);

export const useHasReachedViewsLimit: HookStateFunction<number | null, EntityLimitsHookProp> =
	createStateHook(NavigationStore, {
		selector: getHasReachedLimit,
	});
